import { MaintenanceMessage } from "pwapi";

import {
  BROWSER_FEATURES_SET_AVIF_SUPPORTED,
  LOGIN_SUCCESS,
  SYSTEM_FEATURE_FLAGS_SUCCESS,
  SYSTEM_FEATURE_FLAGS_FAILURE,
  MESSAGES_SUCCESS,
  DEAUTHENTICATE,
  LIS,
} from "../actions/constants";

/**
 * FEATURE FLAGS
 */
export interface FeatureFlags {
  /**
   * If set, a "Restore Image" button appears below the "This slide is not available in storage"
   * message in the slide viewer.
   */
  slide_restoration_process?: boolean;

  // TODO: Add brief documentation for these other flags
  // - What the flag enables
  // - Whether it's been enabled for most users, and currently functions as an emergency rollback mechanism
  // - Whether it can be eventually removed
  // - Whether it's a flag to be used by developers only

  // Viewer
  /**
   * AI Overlay - if this is set, enable AI Overlay button when appropriate
   */
  aiOverlay?: boolean;

  // SlideList
  canViewAIResults?: boolean;

  // Django Admin Page
  djangoAdminLink?: boolean;

  // Ability to flag slides
  canFlagSlides?: boolean;

  // Ability to see the LIS link in the viewer
  canSeeLISLink?: boolean;

  // QC Admin Page
  frontendAdmin?: boolean;
  qaReviews?: boolean;
  bulkFinalizeSignature?: boolean;
  canCompleteLabOrder?: boolean;
  canCancelSentLabOrder?: boolean;
  canCancelAutomatedOrder?: boolean;
  canPrintLabOrders?: boolean;
  canPrintLabOrdersBlockBarcode?: boolean;
  canSortPrintLabOrdersByDoctor?: boolean;
  adminWPLFilters?: boolean;
  limitBillingTypesAdmin?: boolean;

  //QC and viewer screen management
  // Allows a user to undo a screen and reset the case back to the previous state
  canCancelScreen?: boolean;
  // Allows an admin user to setup the case for screening using the administration page
  adminScreenSetup?: boolean;
  // Opens up the ability for a physician to request a screen
  requestScreen?: boolean;
  // Request screens availability for ordering providers
  requestScreenOrderingDoctor?: boolean;

  // Screens can be requested on a per specimen basis
  screenPerSpecimen?: boolean;

  // Flag that allows the QC user to update cases which have been finalized
  editFinalizedCase?: boolean;

  // Flag that allows the QC user to add or edit slidees (but not delete) on cases which have been finalized
  addSlideToFinalizedCase?: boolean;

  // CaseList
  displayCaseTags?: boolean;
  specimenColumn?: boolean;
  statusColumn?: boolean;
  flagColumn?: boolean;
  workflowColumn?: boolean;
  slideRetentionColumn?: boolean;
  hideSlidesCount?: boolean;
  statusCounts?: boolean;
  patientDOBFilter?: boolean;
  assignmentFilter?: boolean;
  actionFilter?: boolean;
  statusFilter?: boolean;
  flaggedFilter?: boolean;
  accessionDateRangeFilter?: boolean;
  reportedDateRangeFilter?: boolean;
  biopsyDateFilter?: boolean;
  diagnosisCategoryFilter?: boolean;
  commentFilter?: boolean;
  icd10Filter?: boolean;
  changeAssignment?: boolean;
  caseReassignment?: boolean;
  caseSubQueues?: boolean;

  //
  caseFinalizeKey?: boolean;

  // FilterList
  debugFilterState?: boolean;

  // MRMC Study to Hide UI Elements
  mrmcStudy?: boolean;

  // Modality 2 flag
  modality2?: boolean;

  // Flag enabling features for the "Sign Out in Viewer" flow
  signOutInViewer?: boolean;

  // Flag for allowing a user to issue a Preliminary report
  issuePrelimReports?: boolean;

  // Flag for opening up the append document code
  useMergeCaseReportWorkflow?: boolean;

  // Flag enabling the specimen edit feature in the viewer for users
  viewerSpecimenEdit?: boolean;

  // Flag enabling prospective AI features (e.g. region of interest tool)
  prospectiveAI?: boolean;

  // Flag enabling stain ordering
  stainOrdering?: boolean;

  // Flag enabling TC/PC modifiers on CPT Codes
  cptCodeModifiers?: boolean;

  // Log interaction listener setup, teardown, and events to console
  debugInteractionListener?: boolean;

  // Flag to test out digitization UI
  testDigitizationCodes?: boolean;

  // Flag to skip the requisition document check
  skipDocCheck?: boolean;

  // Flags for the clinic admin report pages
  clinicReportsList?: boolean;
  bulkReportDownload?: boolean;
  clinicRequisitionsList?: boolean;

  // Flag to show the "Has Flagged Slide" filter
  filterFlaggedSlides?: boolean;

  skipAutomaticBarcodePrinting?: boolean;
  singlePageBarcodePrinting?: boolean;

  canChangeStations?: boolean;
  canChangeLabs?: boolean;
  lisHistoryLink?: boolean;
  lisBarcodesLink?: boolean;
  lisGrossPieceCount?: boolean;

  // Enables Reset Gross Tokens Button in Grossing UI
  resetGrossMacroButton?: boolean;

  // Enables visbility of Cyst Subtype Regions of Interest
  cystSubtypeROI?: boolean;

  // Enables a button to manually throw an error. Meant for testing error tracking integrations
  hasErrorButton?: boolean;

  // Enables increased slide qr code ECC (from "L" to "H")
  increasedSlideECC?: boolean;

  nonSlideDeliverables?: boolean;
  slidelessViewer?: boolean;
  canViewNonSuccessSlides?: boolean;

  slideCountEstimates?: boolean;

  // Enables the "Generate Sendout Requisition" button from LIS Case Details
  sendoutRequisition?: boolean;

  // Enables Digitizations Codes to be Automatically Added to CPT Codes
  addDigitizationCodes?: boolean;
  // Enables forcing -TC/-26 modifiers for bill_tc/bill_pc "only" cases
  forceTCPCModifiers?: boolean;
  // Enables forcing -TC/-26 modifiers for TC-PC Split cases
  genericRuleModifiers?: boolean;

  // Flag to enable site-specific ICD10 codes
  siteSpecificICD10?: boolean;

  switches?: {
    difProtocol?: boolean;
    slowMohsProtocol?: boolean;
    wplDigitizationCodes?: boolean;
    lisDigitizationCodes?: boolean;
  };

  webSocketDebugging?: boolean;

  // Enables selecting a grossed_by_external_lab_id
  externalGrossingLab?: boolean;

  // Enables ordering multiple QRY of UNSTN slides
  unstnOrderQty?: boolean;

  // Enables range-based fully precomputed slide WSIs
  labWorkStatus?: boolean;
  pendingLabWorkWorkingReason?: boolean;

  // Enables alt-clicking elements in the LIS to simulate physical barcode scans
  lisAltClickScanning?: boolean;

  // Allows viewing Cases with status = ordered
  orderedCaseViewer?: boolean;

  // Allows `Accession.case_type` and `Case.type` to be set
  caseType?: boolean;

  // Allows the new "experimental" effective magnification zooming
  // Must be combined with a LabSetting on the lab scanning the slide
  effectiveMagnification?: boolean;

  // Allows barcode scans to proceed to other objects while current objects may be incomplete
  allowIncompleteScanning?: boolean;
  // Same as above but at the cutting station
  allowIncompleteCutScanning?: boolean;
  // Allows "scanning out" slides from the cutting station list
  allowSlideScanOutFromList?: boolean;

  // Disables the closing of the specimen dialog when zooming >1 in the viewer
  dontCloseSpecimenOnZoom?: boolean;

  // Enables "QA Reviews in Viewer" Feature
  qaReviewsInViewer?: boolean;

  // Move the slide macro image under the specimen dialog on larger screens.
  slideMacroUnderSpecimen?: boolean;

  // Allows self-correcting reports; meaning, the current user is the active_assignment
  correctOwnReport?: boolean;
  // Allows correcting any report the user has permissions on; meant for limited lab access
  correctAnyReport?: boolean;

  // Makes a "read only" viewer in the sense that only Technical Reports can be published
  // This should be implemented as some sort of "case setting" depending on characteristics
  // ... of the system as indicated by Generic Rules. In our immediate example, Texas Tech
  // ... users do not diagnose in the Viewer, but instead need to publish a Technical Report
  // ... to finalize a case. Thoughts included a `viewer_signout` boolean on Case orCaseMetadata
  // ... that is GenericUpdated via a Rules Engine. However, a broader "settings" engine (i.e
  // ... GenericSettings) that models feature flags with configurable characteristics is needed.
  // This feature flag diables diagnosis.
  technicalReportsOnly?: boolean;
  // This enables technical reports alongside other reports. Intended for superusers.
  technicalReports?: boolean;

  // Preview Auto CPT Codes
  previewAutoCPTCodes?: boolean;
  // Allows users to mark orders as inadequate
  inadequateOrders?: boolean;
  // Allows users to mark orders with no_charge_reason
  noChargeReason?: boolean;

  // Use the new disclaimers endpoints
  useDatabaseDisclaimers?: boolean;

  // Shows a more visible Self-Pay Double $$ Icon in Red
  doubleRedSelfPay?: boolean;

  // Allows barcode scanning in the viewer
  viewerBarcodeScanning?: boolean;

  // Allows printing a slide "from" an order in the dot admin,
  // ... as if it had been cut at a cutting station
  printSlideFromOrder?: boolean;

  // Experimental Adjustments for new IHC labels
  nonSquareZebraPrint?: boolean;
  slidePrintFitLabelWidth?: boolean;
  ihcSlideReorganization?: boolean;

  // Open Documents in New Window, splitting the screen, instead of new tab
  documentViewWindow?: boolean;

  // When choosing a ViwerItem, Viewer should favor Unviewed Slides
  shouldFavorUnviewedSlides?: boolean;

  // Allows user to filter by and complete/hold cases needing billing entry
  billingEntry?: boolean;

  // When enabled, this flag sorts viewer slides in the order they were placed, with the latest being last.
  slideSorting?: boolean;

  // When enabled, this flag adds a Clear Codes button to the MacroCode control in the Diagnisis Dialog
  canClearDiagnosisFields?: boolean;

  // Allows inline display of previous case diagnoses in Patient Modal
  inlineHistoryDiagnoses?: boolean;
  inlineHistoryMicroscopic?: boolean;
  inlineHistoryNote?: boolean;

  // When enabled, this flag adds peripheral device support to the viewer, such as Space Mouse. A button will show up in the viewer header to toggle any connected devices.
  peripherals?: boolean;
  // Hide User Support Documents in Help & Support Modal-- intended for external educational users
  hideUserSupportDocuments?: boolean;
  // Allows connection to backend websocket for charge caches
  chargeCacheSockets?: boolean;
  // When enabled, allows edge panning in the viewer by moving cursor to viewport edges. Toggle with Shift+P.
  edgePan?: boolean;

  // Hotkeys for Dr. Kwon before they get enabled for everyone else
  kwonKeys?: boolean;

  // Makes use of `orientation` on the Slide
  autoOrientation?: boolean;
  orientationHotkeys?: boolean;
  drawOrientationBbox?: boolean;

  // Start blank diagnoses with macro code from AI
  autoFillAIMacroCode?: boolean;

  // TODO: Removing immediately after cap
  canUseAuditLog?: boolean;
}

export interface SysState {
  browser?: { avifSupported?: boolean };
  features: FeatureFlags;
  featureFlagsInitialized: boolean;
  error?: Error;
  messages?: MaintenanceMessage[]; // TODO: get MaintenanceMessage type from OpenAPI
  caseListSortedIds?: number[];
  taskIds: string[];
}

export const initialState: SysState = {
  browser: {
    avifSupported: true,
  },
  features: {
    switches: {},
  },
  featureFlagsInitialized: false,
  error: undefined,
  messages: [],
  taskIds: [],
};

const reducer = (state: SysState = initialState, action: any): SysState => {
  switch (action.type) {
    case LOGIN_SUCCESS:
      return {
        ...state,
        error: undefined,
      };
    case SYSTEM_FEATURE_FLAGS_SUCCESS:
      return {
        ...state,
        features: action.payload.response,
        featureFlagsInitialized: true,
      };
    case BROWSER_FEATURES_SET_AVIF_SUPPORTED:
      return {
        ...state,
        browser: {
          ...state.browser,
          avifSupported: action.payload,
        },
      };
    case SYSTEM_FEATURE_FLAGS_FAILURE:
      return {
        ...state,
        features: {},
        featureFlagsInitialized: true,
      };
    case MESSAGES_SUCCESS:
      return {
        ...state,
        messages: action.payload,
      };
    case DEAUTHENTICATE:
      // When we log out we want to reset everything except maintenance messages
      return {
        ...initialState,
        messages: state.messages,
      };
    case LIS.CASE.LIST.SUCCESS:
      return {
        ...state,
        caseListSortedIds: action.payload?.response?.results?.map(
          (json: any) => json?.id
        ),
      };
    case LIS.BLOCK.PRINT_CASSETTES.REQUEST_SUCCESS:
      return {
        ...state,
        taskIds: [...state.taskIds, action.payload.response.task_id],
      };
    case LIS.SPECIMEN.PRINT_PIECE_COUNT.SUCCESS:
    case LIS.SPECIMEN.PRINT.SUCCESS:
    case LIS.BLOCK.CREATE.SUCCESS:
    case LIS.BLOCK.DESTROY.SUCCESS:
      if (!action.payload?.response?._meta?.cassette_printing_task_id)
        return state;

      return {
        ...state,
        taskIds: [
          ...state.taskIds,
          action.payload.response._meta.cassette_printing_task_id,
        ],
      };
    case LIS.BLOCK.PRINT_CASSETTES.SUCCESS:
    case LIS.BLOCK.PRINT_CASSETTES.ERROR:
      return {
        ...state,
        taskIds: state.taskIds.filter(
          (taskId) => taskId !== action.payload.task_id
        ),
      };
    default:
      return state;
  }
};

export const selectFeatureFlag = (
  { sys }: { sys: SysState },
  flag: keyof FeatureFlags
) => !!sys.features[flag];
// TODO: would be internal API to select multiple flags at the same time if needed?
// selectFeatureFlags

export default reducer;
